import React, { useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { Popover } from '@/components/ui/popover';
import { DelayedSpinner } from '@/components/ui/delayed-spinner';
import { useFrameTime } from '@/components/ui/hover-time';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

// One «биатлон» dot on the all-branches zone card. Hover (fine pointer) or tap
// (coarse) opens the standard mini unit card — name, photo, capture time —
// anchored at the dot. The parent maps its DTOs onto DotUnit so this stays the
// single dot+popover implementation for racks, tables and queues alike.

export interface DotUnit {
  key: string;
  name: string;
  /** Shared status-dot colour class (SAMPLE_DOT / STATUS_GREEN family). */
  dotClass: string;
  statusLabel: string;
  /** Absolutised + signature-stabilised URL (parent runs imgSrc). */
  imageUrl: string | null;
  /** Timestamp shown top-right as HH:MM — capture time for camera units,
   *  due time for tasks. */
  capturedAt: string | null;
  /** Unit kind has no photo evidence at all (tasks) — skip the image slot
   *  instead of rendering the ImageOff placeholder. */
  photoless?: boolean;
}

// The card body is one big click-to-drill-down target, and the popover's own
// outside-click dismissal fires BEFORE that click lands — so a tap made to
// close a popover would also navigate. The card consults this to swallow
// exactly that click.
let lastDismissAt = 0;
export function popoverJustDismissed(): boolean {
  return Date.now() - lastDismissAt < 350;
}

const HOVER_CLOSE_MS = 150;
// Open only after the pointer LINGERS — so sweeping the mouse across a row of
// dots doesn't flash every popover open in turn (Alexey 2026-07-21).
const HOVER_OPEN_MS = 200;

function UnitMiniCard({
  unit,
  onPointerEnter,
  onPointerLeave,
}: {
  unit: DotUnit;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}) {
  const frameTime = useFrameTime();
  const [loaded, setLoaded] = useState(false);
  const time = frameTime(unit.capturedAt);
  return (
    <div
      className="w-56 p-2.5"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      // A tap inside the popover must never bubble into the card's drill-down.
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-start gap-1.5">
        <span className={cn('mt-1 size-2 shrink-0 rounded-full', unit.dotClass)} />
        {/* Wrap the full name/label — truncation cut long task text (Alexey). */}
        <span className="min-w-0 flex-1 break-words text-sm font-medium">{unit.name}</span>
        {time && (
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{time}</span>
        )}
      </div>
      <div className="mt-0.5 break-words text-xs text-muted-foreground">{unit.statusLabel}</div>
      {!unit.photoless &&
        (unit.imageUrl ? (
          <div className="relative mt-2 min-h-20 overflow-hidden rounded-md bg-muted">
            {!loaded && <DelayedSpinner />}
            <img
              src={unit.imageUrl}
              alt={unit.name}
              draggable={false}
              className={cn(
                'block max-h-48 w-full object-contain transition-opacity duration-200',
                loaded ? 'opacity-100' : 'opacity-0',
              )}
              onLoad={() => setLoaded(true)}
              onError={() => setLoaded(true)}
            />
          </div>
        ) : (
          <div className="mt-2 flex h-20 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <ImageOff className="size-6" />
          </div>
        ))}
    </div>
  );
}

export function UnitDotPopover({ unit }: { unit: DotUnit }) {
  const coarse = useMediaQuery('(pointer: coarse)');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const openTimer = useRef<number | null>(null);

  const cancelOpen = () => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };
  const scheduleOpen = () => {
    cancelOpen();
    openTimer.current = window.setTimeout(() => setOpen(true), HOVER_OPEN_MS);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    cancelOpen(); // leaving before the open delay elapsed → don't open at all
    closeTimer.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_MS);
  };

  React.useEffect(() => () => { cancelOpen(); cancelClose(); }, []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`${unit.name} — ${unit.statusLabel}`}
        // size-5 hit target around a size-2.5 dot: tappable on mobile without
        // inflating the visual dot beyond the dashboard's shared scale.
        className="flex size-5 shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={e => {
          e.stopPropagation();
          cancelClose();
          cancelOpen();
          // Coarse pointer: tap toggles. Fine pointer: a click opens immediately
          // (no hover-lag) — and must not flip it back shut.
          setOpen(o => (coarse ? !o : true));
        }}
        // Fine pointer: open only after a lingering delay (no flash on pass-by).
        onPointerEnter={coarse ? undefined : () => { cancelClose(); scheduleOpen(); }}
        onPointerLeave={coarse ? undefined : scheduleClose}
        // :focus-visible gates this to KEYBOARD focus — a tap also focuses the
        // button, and an unconditional open here + the click toggle above would
        // blink the popover open-then-shut on the first mobile tap.
        onFocus={e => {
          if (e.target.matches(':focus-visible')) setOpen(true);
        }}
        // Coarse: a tap inside the popover content blurs the button (Android),
        // which must not dismiss what the user is reading — outside-tap close
        // is the Popover's own job there.
        onBlur={coarse ? undefined : scheduleClose}
      >
        <span className={cn('size-2.5 rounded-full', unit.dotClass)} aria-hidden />
      </button>
      <Popover
        open={open}
        onOpenChange={next => {
          // Only the Popover itself calls this (outside click / Escape) —
          // record it so the card can swallow the same click.
          if (!next) lastDismissAt = Date.now();
          setOpen(next);
        }}
        triggerRef={triggerRef}
        side="bottom"
        align="center"
      >
        <UnitMiniCard
          unit={unit}
          onPointerEnter={coarse ? () => {} : cancelClose}
          onPointerLeave={coarse ? () => {} : scheduleClose}
        />
      </Popover>
    </>
  );
}
